import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import FilteredTableCore from "../../../components/Organismos/Table/FilteredTableCore";
import type { IHeader } from "../../../components/Organismos/Table/FilteredTable.types";
import {
  FIELDS_TO_IGNORE,
  getExcelColumnTitle,
} from "../../../components/Organismos/Table/FilteredTable.utils";
import Button from "../../../components/staff/Button";
import { useDynamicPageMeta } from "../../../hooks/useDynamicPageMeta";
import IIntegratorRunLog from "../../../interfaces/models/IIntegratorRunLog";
import { getAllFirmNames } from "../../../Services/Firm";
import { firmShortLabel } from "../../../utils/firmDisplayName";
import { setIntegratorRunLogStaffReview } from "../../../Services/IntegratorRunLog";
import { defaultPageSize } from "../../../utils/const";
import { formatRegionDisplay } from "../../../utils/formatRegionDisplay";
import { toastError, toastSuccess } from "../../../utils/toast";
import { buildIntegratorRunLogFiltersData } from "./integratorRunLogFilters";
import {
  formatStaffReviewDisplay,
  isRunLogClosed,
  statusClassName,
  statusLabel,
  triggerLabel,
} from "./runLogDisplay";

const EMPTY = "—";

const ACTION_BUTTON_CLASS =
  "!w-auto shrink-0 whitespace-nowrap !px-3 !py-1.5 text-sm";

function formatDateTime(ms?: number): string {
  if (!ms) {
    return EMPTY;
  }
  return new Date(ms).toLocaleString("en-US");
}

function firmDisplayName(
  firmId: string,
  namesById: Map<string, string>
): string {
  return namesById.get(firmId) || firmId;
}

function runLogReviewKey(log: IIntegratorRunLog): string {
  return `${log.firmId}:${log.id}`;
}

export default function ListIntegratorRunLogs(): ReactElement {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setPageMeta, clearPageMeta } = useDynamicPageMeta();
  const firmId = searchParams.get("firmId")?.trim() || undefined;

  const [firmNameById, setFirmNameById] = useState<Map<string, string>>(
    new Map()
  );
  const [firmRegionById, setFirmRegionById] = useState<Map<string, string>>(
    new Map()
  );
  const [firmOptions, setFirmOptions] = useState<
    { id: string; name: string }[]
  >([]);
  const [listVersion, setListVersion] = useState(0);
  const [reviewBusyKey, setReviewBusyKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const firms = await getAllFirmNames();
      if (cancelled) {
        return;
      }
      setFirmNameById(
        new Map(
          firms.map((firm) => [firm.id, firmShortLabel(firm) || firm.id])
        )
      );
      setFirmRegionById(
        new Map(
          firms.map((firm) => [
            firm.id,
            formatRegionDisplay(firm.regionNames) || EMPTY,
          ])
        )
      );
      setFirmOptions(
        firms
          .map((firm) => ({
            id: firm.id,
            name: (firmShortLabel(firm) || firm.id).trim(),
          }))
          .filter((firm) => firm.name)
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredFirmName = firmId ? firmNameById.get(firmId) : undefined;

  useEffect(() => {
    setPageMeta({
      title:
        firmId && filteredFirmName
          ? `Run logs — ${filteredFirmName}`
          : "Run logs",
    });
    return () => clearPageMeta();
  }, [firmId, filteredFirmName, setPageMeta, clearPageMeta]);

  const filtersData = useMemo(
    () => buildIntegratorRunLogFiltersData(firmOptions),
    [firmOptions]
  );

  const pinnedInputFilters = useMemo(() => {
    if (!firmId) {
      return undefined;
    }
    const name = firmNameById.get(firmId) || firmId;
    return {
      Firm: [{ id: firmId, name }],
    };
  }, [firmId, firmNameById]);

  const defaultQuickFilterSeed = useMemo(() => {
    if (!firmId) {
      return undefined;
    }
    return {
      inputFilters: {
        Firm: [{ id: firmId, name: firmNameById.get(firmId) || firmId }],
      },
      query: {
        firmId: { value: [firmId], comparisonType: "IN" },
      },
    };
  }, [firmId, firmNameById]);

  const handleStaffReview = useCallback(
    async (log: IIntegratorRunLog, reviewed: boolean) => {
      if (reviewBusyKey) {
        return;
      }
      const key = runLogReviewKey(log);
      setReviewBusyKey(key);
      try {
        await setIntegratorRunLogStaffReview({
          id: log.id,
          firmId: log.firmId,
          reviewed,
        });
        toastSuccess(
          reviewed ? "Run log marked reviewed." : "Review cleared."
        );
        setListVersion((version) => version + 1);
      } catch (error: any) {
        toastError(error?.message ?? "Could not update staff review.", {
          autoClose: 6000,
        });
      } finally {
        setReviewBusyKey(null);
      }
    },
    [reviewBusyKey]
  );

  const columnsDef: IHeader[] = useMemo(
    () => [
      { id: "firmName", header: "Firm", enableSorting: false },
      {
        id: "region",
        header: "Region",
        enableSorting: false,
        width: 160,
        defaultVisibility: false,
      },
      {
        id: "status",
        header: "Status",
        width: 130,
        cell: ({ row }) => {
          const log = row as IIntegratorRunLog;
          return (
            <span
              className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusClassName(log.status)}`}
            >
              {statusLabel(log.status)}
            </span>
          );
        },
      },
      { id: "trigger", header: "Trigger", enableSorting: false, width: 200 },
      {
        id: "queuedDate",
        header: "Queued",
        width: 180,
        cell: ({ row }) => formatDateTime(row.queuedDate),
      },
      {
        id: "finished",
        header: "Finished",
        enableSorting: false,
        width: 180,
        cell: ({ row }) =>
          formatDateTime(row.finishedDate ?? row.skippedDate),
      },
      {
        id: "staffReviewed",
        header: "Reviewed",
        enableSorting: false,
        width: 200,
        cell: ({ row }) => {
          const log = row.runLog as IIntegratorRunLog;
          const text = formatStaffReviewDisplay(log);
          return (
            <span className="text-sm text-gray-700">{text ?? EMPTY}</span>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        width: 280,
        cell: ({ row }) => {
          const log = row.runLog as IIntegratorRunLog;
          const busy = reviewBusyKey === runLogReviewKey(log);
          const closed = isRunLogClosed(log.status);
          const reviewed = Boolean(log.staffReviewedDate);
          return (
            <div className="flex flex-wrap gap-2">
              <Button
                text="View"
                color="neutral"
                variant="outlined"
                size="fixed"
                className={ACTION_BUTTON_CLASS}
                onClick={() =>
                  navigate(`/integrator-engine/run-logs/${row.id}`, {
                    state: row.runLog,
                  })
                }
              />
              {closed && (
                <Button
                  text={
                    busy
                      ? "Saving…"
                      : reviewed
                        ? "Clear review"
                        : "Mark reviewed"
                  }
                  color="neutral"
                  variant="outlined"
                  size="fixed"
                  className={ACTION_BUTTON_CLASS}
                  disabled={Boolean(reviewBusyKey)}
                  onClick={() => void handleStaffReview(log, !reviewed)}
                />
              )}
            </div>
          );
        },
      },
    ],
    [navigate, reviewBusyKey, handleStaffReview]
  );

  const excelHeaders = useMemo(
    () =>
      columnsDef
        .filter((col) => !FIELDS_TO_IGNORE.has(col.id))
        .map((col) => getExcelColumnTitle(col)),
    [columnsDef]
  );

  const formatData = useCallback(
    (rows: IIntegratorRunLog[]) =>
      rows.map((row) => ({
        ...row,
        firmName: firmDisplayName(row.firmId, firmNameById),
        region: firmRegionById.get(row.firmId) ?? EMPTY,
        trigger: triggerLabel(row),
        staffReviewed: formatStaffReviewDisplay(row) ?? EMPTY,
        runLog: row,
      })),
    [firmNameById, firmRegionById]
  );

  return (
    <div className="flex flex-col min-w-0">
      <FilteredTableCore
        key={listVersion}
        columnsDef={columnsDef}
        showRowNumber
        endpoint="/integratorRunLog"
        filtersData={filtersData}
        formatData={formatData}
        excelHeaders={excelHeaders}
        excelFilename="integrator-run-logs"
        inlineFilterToolbar
        showSelectedFilters
        pinnedInputFilters={pinnedInputFilters}
        defaultQuickFilterSeed={defaultQuickFilterSeed}
        persistFilters={false}
        defaultSorting={{ field: "queuedDate", order: "desc" }}
        defaultPageSize={defaultPageSize}
        emptyStateMessage="No run logs found"
      />
    </div>
  );
}
